/**
 * The Packages tab's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/membership-credits/packages` (all optional; a default is never written):
 * - `q`: search on the package name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends packages in no defined order, so the
 *   default is Package name ascending.
 */
import type { Offering, OfferingCredit } from '@/api/offerings';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const PACKAGES_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type PackageSortKey = 'name' | 'price' | 'contents' | 'status';
export const PACKAGE_SORT_KEYS: readonly PackageSortKey[] = ['name', 'price', 'contents', 'status'];
export const PACKAGE_DEFAULT_SORT: SortState<PackageSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a package (its name, falling back to the id). */
export function packageName(pkg: Offering): string {
  return pkg.name.length > 0 ? pkg.name : pkg.id;
}

/** One grant rendered as "amount × Bucket" (bucket name, falling back to its id). */
export function creditLabel(credit: OfferingCredit): string {
  const name = credit.bucketName.length > 0 ? credit.bucketName : credit.creditBucketId;
  return `${credit.amount} × ${name}`;
}

/** The Contents summary: the first grant + "+N more", or `null` when it grants nothing. */
export function packageContentsLabel(pkg: Offering): string | null {
  const first = pkg.credits[0];
  if (!first) return null;
  const more = pkg.credits.length - 1;
  return `${creditLabel(first)}${more > 0 ? ` +${more} more` : ''}`;
}

/** The lifecycle Status chip label. */
export function packageStatusLabel(pkg: Offering): string {
  if (pkg.status === 'DRAFT') return 'Draft';
  if (pkg.status === 'INACTIVE') return 'Inactive';
  return 'Active';
}

/**
 * What a package row shows (and sorts by) in each column. An unpriced package ("No
 * price") and one with no credits ("No credits") sort with the blanks (last).
 */
export function packageSortValue(pkg: Offering, key: PackageSortKey): SortValue {
  switch (key) {
    case 'name':
      return packageName(pkg);
    case 'price':
      return pkg.price;
    case 'contents':
      return packageContentsLabel(pkg);
    case 'status':
      return packageStatusLabel(pkg);
  }
}

/** The Packages search: the package name. */
export function packageMatches(pkg: Offering, query: string): boolean {
  return matchesSearch(query, [packageName(pkg)]);
}
